/**
 * A read-only preview of a committed case file, for the upload modal's
 * side-by-side duplicate comparison.
 *
 * `StagedFilePreview` renders local bytes with the app's native readers; this
 * fetches a live node's bytes (its original upload, or the server PDF for a
 * legacy-Office file) and hands them to the very same component, so the match
 * beside a staged duplicate reads with an identical viewer. It is a stripped,
 * chrome-free cousin of `FilePane` — no editing, no version menu, no selection
 * wiring — because a comparison pane only needs to show the file.
 */
import { BrandLoader } from "@/components/brand";
import { useEffect, useState, type FC } from "react";

import { downloadFile } from "../../../api/OpenAIApi";
import { FilesystemAPI } from "../../caseFiles/api/filesystemApi";
import { filePreviewKind } from "../../caseFiles/filePreview";
import { nodeFilename } from "../../caseFiles/nodeNames";
import type { FsNode } from "../../caseFiles/types";
import { useCaseIde } from "../context/CaseIdeContext";
import { StagedFilePreview } from "./StagedFilePreview";

type Props = { node: FsNode };

const Loading: FC = () => (
  <div className="flex h-full items-center justify-center">
    <BrandLoader label="Loading file" />
  </div>
);

const Notice: FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="flex h-full items-center justify-center px-6 text-center text-sm text-muted-foreground">
    {children}
  </div>
);

/** Which bytes to draw: the original upload, or the server PDF for old Office. */
const bytesSourceFor = (node: FsNode): "original" | "pdf" | null => {
  if (!node.storage_path) return null;
  const kind = filePreviewKind(node);
  if (kind === "legacy-office") return "pdf";
  if (kind === "external") return null;
  return "original";
};

export const NodePreview: FC<Props> = ({ node }) => {
  const { individualId } = useCaseIde();
  const [blob, setBlob] = useState<Blob | null>(null);
  const [error, setError] = useState<string | null>(null);
  const source = bytesSourceFor(node);
  const storagePath = node.storage_path ?? null;

  useEffect(() => {
    let cancelled = false;
    setBlob(null);
    setError(null);
    if (!source || !storagePath) return undefined;
    const fetchBytes =
      source === "pdf"
        ? FilesystemAPI.getNodePdf(individualId, node.id)
        : downloadFile(storagePath);
    fetchBytes
      .then((loaded: Blob) => !cancelled && setBlob(loaded))
      .catch(() => !cancelled && setError("Couldn't load this file."));
    return () => {
      cancelled = true;
    };
  }, [source, storagePath, node.id, individualId]);

  if (!source) {
    return <Notice>This file can&rsquo;t be previewed here.</Notice>;
  }
  if (error) return <Notice>{error}</Notice>;
  if (!blob) return <Loading />;
  return (
    <StagedFilePreview
      file={blob}
      name={nodeFilename(node)}
      id={`node:${node.id}`}
    />
  );
};
